import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Fade,
  Zoom,
  Slide,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle,
  Celebration,
  Dashboard,
  Star,
  Apartment,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { AppHeader } from '../components/layout/AppHeader';

const CongratulationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authState } = useAuth();
  
  const [showContent, setShowContent] = useState(false);
  const [showButton, setShowButton] = useState(false);

  // Animation sequence
  useEffect(() => {
    const timer1 = setTimeout(() => setShowContent(true), 300);
    const timer2 = setTimeout(() => setShowButton(true), 1500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const handleGoToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  const userName = authState.user?.full_name?.split(' ')[0] || 'Usuário';

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <AppHeader />
      
      {/* Animated background elements */}
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {[...Array(6)].map((_, i) => (
          <Zoom 
            key={i} 
            in={showContent} 
            timeout={1000 + i * 200}
            style={{ transitionDelay: `${i * 100}ms` }}
          >
            <Star 
              sx={{ 
                position: 'absolute',
                color: theme.palette.primary.main,
                opacity: 0.3,
                fontSize: Math.random() * 30 + 20,
                top: `${Math.random() * 80 + 10}%`,
                left: `${Math.random() * 80 + 10}%`,
                animation: 'float 3s ease-in-out infinite',
                animationDelay: `${i * 0.5}s`,
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-10px)' }
                }
              }} 
            />
          </Zoom>
        ))}
      </Box>

      <Container maxWidth="md" sx={{ py: 8, position: 'relative', zIndex: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          textAlign: 'center',
          minHeight: '70vh',
          justifyContent: 'center'
        }}>
          
          {/* Success Icon */}
          <Zoom in={showContent} timeout={800}>
            <Avatar sx={{ 
              width: 120, 
              height: 120, 
              bgcolor: theme.palette.success.main,
              mb: 4,
              boxShadow: `0 8px 32px ${theme.palette.success.main}40`
            }}>
              <CheckCircle sx={{ fontSize: 80, color: 'white' }} />
            </Avatar>
          </Zoom>

          {/* Main Content */}
          <Fade in={showContent} timeout={1000}>
            <Box sx={{ mb: 6 }}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 'bold',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Parabéns, {userName}! 🎉
              </Typography>
              
              <Typography variant="h5" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
                Seu plano Muu Plus foi ativado com sucesso!
              </Typography>
              
              <Typography variant="body1" sx={{ 
                fontSize: '1.1rem', 
                lineHeight: 1.6, 
                maxWidth: 600, 
                mx: 'auto',
                mb: 4
              }}>
                Agora você tem acesso completo à nossa plataforma de gestão pecuária. 
                Comece a gerenciar seu rebanho, acompanhar a saúde dos animais e 
                gerar relatórios detalhados para otimizar sua propriedade rural.
              </Typography>
            </Box>
          </Fade>

          {/* Features Card */}
          <Slide direction="up" in={showContent} timeout={1200}>
            <Card sx={{ 
              maxWidth: 500, 
              mb: 6,
              boxShadow: theme.shadows[8],
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
                  <Apartment sx={{ fontSize: '2.5rem', color: theme.palette.primary.main, mr: 2 }} />
                  <Typography variant="h5" fontWeight="bold">
                    Muu Plus Ativo
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                  Você agora tem acesso a:
                </Typography>
                
                <Box sx={{ textAlign: 'left' }}>
                  {[
                    'Dashboard completo com métricas avançadas',
                    'Gestão ilimitada do rebanho',
                    'Relatórios detalhados e exportação',
                    'Suporte prioritário 24/7'
                  ].map((feature, index) => (
                    <Box key={index} display="flex" alignItems="center" sx={{ mb: 1.5 }}>
                      <CheckCircle sx={{ 
                        color: theme.palette.success.main, 
                        fontSize: '1.2rem', 
                        mr: 2 
                      }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Slide>

          {/* Action Button */}
          <Zoom in={showButton} timeout={600}>
            <Button
              onClick={handleGoToDashboard}
              variant="contained"
              size="large"
              startIcon={<Dashboard />}
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 3,
                boxShadow: theme.shadows[8],
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[12],
                },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              Ir para o Dashboard
            </Button>
          </Zoom>

          {/* Additional Info */}
          <Fade in={showButton} timeout={800}>
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                💡 <strong>Dica:</strong> Explore todas as funcionalidades no seu dashboard. 
                Se precisar de ajuda, nossa equipe está sempre disponível!
              </Typography>
            </Box>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
};

export default CongratulationsPage;